import { api } from './api'
import { Template, PaginatedResponse } from '@/types'

export interface TemplateFilters {
  search?: string
  page?: number
  limit?: number
}

export interface CreateTemplateRequest {
  name: string
  subject: string
  bodyHtml: string
  bodyText?: string
  variables?: string[]
}

export interface UpdateTemplateRequest {
  name?: string
  subject?: string
  bodyHtml?: string
  bodyText?: string
  variables?: string[]
}

export interface DuplicateTemplateRequest {
  name: string
}

export const templateService = {
  async fetchTemplates(filters: TemplateFilters = {}): Promise<PaginatedResponse<Template>> {
    // Backend returns: { success: true, data: Template[], pagination: {...} }
    const response = await api.get<{
      success: boolean
      data: Template[]
      pagination: {
        page: number
        limit: number
        total: number
        totalPages: number
      }
    }>('/templates', {
      params: filters,
    })
    return {
      data: response.data.data || [],
      pagination: response.data.pagination || { page: 1, limit: 10, total: 0, totalPages: 0 },
    }
  },

  async getTemplate(id: number): Promise<Template> {
    // Backend returns: { success: true, data: Template }
    const response = await api.get<{ success: boolean; data: Template }>(`/templates/${id}`)
    return response.data.data
  },

  async createTemplate(data: CreateTemplateRequest): Promise<Template> {
    // Backend returns: { success: true, data: Template }
    const response = await api.post<{ success: boolean; data: Template }>('/templates', data)
    return response.data.data
  },

  async updateTemplate(id: number, data: UpdateTemplateRequest): Promise<Template> {
    // Backend returns: { success: true, data: Template, message: string }
    const response = await api.put<{ success: boolean; data: Template }>(`/templates/${id}`, data)
    return response.data.data
  },

  async deleteTemplate(id: number): Promise<void> {
    await api.delete(`/templates/${id}`)
  },

  async duplicateTemplate(id: number, data: DuplicateTemplateRequest): Promise<Template> {
    // Backend returns: { success: true, data: Template }
    const response = await api.post<{ success: boolean; data: Template }>(`/templates/${id}/duplicate`, data)
    return response.data.data
  },

  async previewTemplate(id: number, variables: Record<string, string> = {}): Promise<{
    subject: string
    htmlPreview: string
    textPreview?: string
  }> {
    const response = await api.post(`/templates/${id}/preview`, { data: variables })
    const { subject, bodyHtml, bodyText } = response.data.data
    return {
      subject,
      htmlPreview: bodyHtml,
      textPreview: bodyText,
    }
  },

  async sendTestEmail(id: number, data: {
    recipientEmail: string
    variables: Record<string, string>
  }): Promise<void> {
    await api.post(`/templates/${id}/test`, data)
  },
}
